import { EntityAdapter } from '@ngrx/entity';
import { get, isEmpty } from 'lodash';

import { ScreenState } from './states';
/**
 * Update the entity with the provided changes as parameter
 * @param state
 * @param changes
 * @param payload
 * @param adapter
 * @returns {object}
 */
export function updateEntity<T>(
  state: any,
  changes: any,
  payload: any,
  adapter: EntityAdapter<ScreenState<T>>
) {
  return adapter.updateOne(
    {
      id: payload.id,
      changes: changes
    },
    state
  );
}
/**
 * Update the value of a relation for the owner screen
 * @param state
 * @param payload
 * @param adapter
 */
export function updatePropertyValue<T>(
  state: any,
  payload: any,
  adapter: EntityAdapter<ScreenState<T>>
) {
  const id = payload.targetCtx.rootId || payload.targetCtx.screenId;
  if (state.entities[id]) {
    const value = { ...state.entities[id].value };
    value[payload.targetCtx.roleName] = payload.value;
    const changes = {
      value: { ...value },
      propertyToModify: payload.propertyToModify
    };
    return adapter.updateOne(
      {
        id: id,
        changes: changes
      },
      state
    );
  } else {
    return state;
  }
}

/**
 * Set a new value to a given property that is indexed by key
 * @param state
 * @param id
 * @param payload
 * @param adapter
 */
export function updateFieldPossibleValues<T>(
  state: any,
  id: string,
  payload: any,
  adapter: EntityAdapter<ScreenState<T>>
) {
  if (state.entities[id]) {
    const entity = state.entities[id];
    const possibleValues = entity['possibleValues'] || {};
    let fieldKey = payload.field;
    if (payload.vars && payload.vars.hasOwnProperty("index")) {
      fieldKey = `${fieldKey}${payload.vars.index}`;

    }
    possibleValues[fieldKey] = payload.possibleValues;
    if (!payload.formatPossibleValuesOff) {
      const value = payload.possibleValues.map(item => ({
        value: item instanceof Object ? { ...item } : item,
        label:
          item instanceof Object
            ? (payload.keys &&
              payload.keys
                .map(elm => get(item, elm))
                .join(payload.separator)) ||
            item
            : item
      }));
      possibleValues[fieldKey] = value;
    }
    const changes = { loading: false, possibleValues: { ...possibleValues } };
    return adapter.updateOne({ id, changes }, state);
  } else {
    return state;
  }
}

/**
 * Initialise the value of an entity
 * @param state
 * @param payload
 * @param adapter
 */
export function initValue<T>(
  state: any,
  payload: any,
  adapter: EntityAdapter<ScreenState<T>>
) {
  const changes = {
    value: payload.result,
    loading: false,
    propertyToModify: undefined

  };
  const id = payload.id;
  return adapter.updateOne(
    {
      id: id,
      changes: changes
    },
    state
  );
}

/**
 * get the code value
 * @param obj
 */

export function getCodeValue(obj: any) {
  return obj.code ? obj.code : obj.id;
}

/**
 * Initialise the value of collection screen with the following structure:
 * {ids: [], entities: {} }
 * @param state
 * @param payload
 * @param adapter
 */
export function initValueEntities<T>(state: any, payload: any, adapter: any) {
  const id = payload.id;
  if (state.entities[id]) {
    let mapItems = [];
    if (payload.result && payload.result instanceof Array) {
      mapItems = payload.result.reduce((obj, item) => {
        obj[getCodeValue(item)] = item;
        return obj;
      }, {});
    }
    const changes = {
      value: {
        ids: [],
        entities: { ...mapItems }
      },
      loading: false
    };
    return adapter.updateOne(
      {
        id: id,
        changes: changes
      },
      state
    );
  } else {
    return state;
  }
}
/**
 * Initialise an empty value of an entity and disable the loading in
 * case of Error dispatched action
 * @param state
 * @param payload
 * @param adapter
 */
export function cancelLoading<T>(
  state: any,
  payload: any,
  adapter: EntityAdapter<ScreenState<T>>
) {
  const changes = {
    value: {},
    loading: false
  };
  const id = payload.id;
  return adapter.updateOne(
    {
      id: id,
      changes: changes
    },
    state
  );
}
/**
 * Update the entities value map of a collection screen
 * @param state
 * @param payload
 * @param adapter
 */
export function updateOneRelationItems<T>(
  state: any,
  payload: any,
  adapter: EntityAdapter<ScreenState<T>>
) {
  const id = findScreen(payload, state);
  if (state.entities[id]) {
    const value = { ...state.entities[id].value };
    const relationData = payload.value instanceof Array ? [...payload.value] : { ...payload.value };
    delete relationData['payload'];
    delete relationData['targetCtx'];

    const code = relationData.code
      ? relationData.code
      : payload.code
        ? payload.code
        : getTemporaryCode();

    relationData instanceof Array
      ? (value.entities = relationData)
      : payload.overrideValue
        ? (value.entities = { [code]: relationData })
        : (value.entities[code] = relationData);

    const changes = {
      value: { ...value }
    };
    return adapter.updateOne(
      {
        id: id,
        changes: changes
      },
      state
    );
  } else {
    return state;
  }
}
/**
 * Remove an item from the value of collection screen in case of relation
 * with an owner entity
 * @param state
 * @param payload
 * @param adapter
 */
export function removeRelationItem<T>(
  state: any,
  payload: any,
  adapter: EntityAdapter<ScreenState<T>>
) {
  const id = payload.id;
  const value = { ...state.entities[id].value };
  delete value.entities[payload.rowId];
  const changes = {
    value: { ...value }
  };
  return adapter.updateOne(
    {
      id: id,
      changes: changes
    },
    state
  );
}
/**
 * Find the entity from the state according to the privided screen
 * id that can aggregate parent screens such as: 908033,345454
 * @param payload
 * @param state
 */
function findScreen(payload: any, state: any) {
  if (payload.targetCtx.ids) {
    return payload.targetCtx.ids.split(',').filter(id => state.entities[id]);
  }
  return payload.targetCtx.screenId;
}
/**
 * Update the all value of a relation for the owner screen
 * @param state
 * @param payload
 * @param relation
 * @param adapter
 */
export function updateRelation<T>(
  state: any,
  payload: any,
  relation: string,
  adapter: EntityAdapter<ScreenState<T>>
) {
  const id = payload.targetCtx.rootId;
  if (state.entities[id]) {
    const value = { ...state.entities[id].value };
    value[relation] = getPayloadValue(payload.value);
    const changes = {
      value: { ...value }
    };
    return adapter.updateOne(
      {
        id: id,
        changes: changes
      },
      state
    );
  } else {
    return state;
  }
}
export function getPayloadValue(value) {
  delete value['payload'];
  delete value['targetCtx'];
  return value;
}
export function getTemporaryCode() {
  return 'TEMP_' + crypto.getRandomValues(new Uint32Array(1));
}
export function removeTemporaryCode(item: any) {
  let newObj = item instanceof Array ? [...item] : { ...item };
  if (newObj instanceof Array) {
    newObj = newObj.map(data => {
      return removeCodeProperty(data);
    });
  } else {
    return removeCodeProperty(newObj);
  }
}
/**
 * Remove the code property for new object created client side
 * @param item
 */
function removeCodeProperty(item: any) {
  if (item.code && item.code.startsWith('TEMP')) {
    delete item.code;
  }
  return item;
}

export function getValueByCode(value, code, roleName?) {
  return value instanceof Array
    ? value.filter(element => {
      return element.code === code;
    })[0]
    : value[roleName];
}

/**
 *  Update the whole value by the provided page's data

 */
export function getVirtualLazyValue(state, payload) {
  const currentState = state.entities[payload.id];
  const data = payload.value;
  let currentValue = [...currentState.value];
  if (isEmpty(currentValue) || currentState.totalItems !== currentValue.length) {
    currentValue = Array.from({ length: currentState.totalItems });
  }
  const startIndex = (payload.vars.page - 1) * payload.vars.size;
  Array.prototype.splice.apply(currentValue, [...[startIndex, payload.vars.size], ...data]);
  const changes = {
    value: [...currentValue],
    loading: false,
    vars: {
      ...payload.vars,
      size: payload.vars.size,
      page: payload.vars.page,
    }
  };
  return {
    id: payload.id,
    changes: changes
  }
}